import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import type { LiveProctoringEventItem, LiveProctoringStudentItem } from "./testTypes";
import { fetchLiveProctoringFeed } from "./testUtils";
import {
  buildProctoringWebSocketUrl,
  parseProctoringWebSocketMessage,
} from "./proctoringWebSocket";

type LiveProctoringStats = {
  active_sessions: number;
  violated_students: number;
  students_with_violations: number;
  total_violation_events: number;
  max_violation_attempts: number;
};

type UseLiveProctoringFeedOptions = {
  testId: string;
  pollIntervalMs?: number;
  paused?: boolean;
  onNewViolations?: () => void;
};

const FALLBACK_POLL_MS = 15000;
const WS_RECONNECT_MS = 4000;

const stopLiveTransport = (
  wsRef: MutableRefObject<WebSocket | null>,
  reconnectTimerRef: MutableRefObject<number | null>,
  setIsLiveConnected: (value: boolean) => void
) => {
  if (reconnectTimerRef.current != null) {
    window.clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
  }
  wsRef.current?.close();
  wsRef.current = null;
  setIsLiveConnected(false);
};

export const useLiveProctoringFeed = ({
  testId,
  pollIntervalMs = 3000,
  paused = false,
  onNewViolations,
}: UseLiveProctoringFeedOptions) => {
  const [events, setEvents] = useState<LiveProctoringEventItem[]>([]);
  const [students, setStudents] = useState<LiveProctoringStudentItem[]>([]);
  const [stats, setStats] = useState<LiveProctoringStats>({
    active_sessions: 0,
    violated_students: 0,
    students_with_violations: 0,
    total_violation_events: 0,
    max_violation_attempts: 4,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  const [isLiveConnected, setIsLiveConnected] = useState(false);
  const [isLiveFeedActive, setIsLiveFeedActive] = useState<boolean | null>(null);
  const [isPollingHealthy, setIsPollingHealthy] = useState(false);
  const [latestViolation, setLatestViolation] =
    useState<LiveProctoringEventItem | null>(null);
  const latestEventAtRef = useRef<string | null>(null);
  const knownEventIdsRef = useRef<Set<string>>(new Set());
  const lastViolationTotalRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const onNewViolationsRef = useRef(onNewViolations);
  const liveFeedActiveRef = useRef<boolean | null>(null);

  useEffect(() => {
    onNewViolationsRef.current = onNewViolations;
  }, [onNewViolations]);

  const mergeEvents = useCallback((incoming: LiveProctoringEventItem[]) => {
    if (incoming.length === 0) return;
    setEvents((prev) => {
      const seen = new Set(prev.map((row) => row.id));
      const merged = [...incoming.filter((row) => !seen.has(row.id)), ...prev];
      return merged.slice(0, 300);
    });
  }, []);

  const applyRealtimeViolation = useCallback(
    (event: LiveProctoringEventItem) => {
      if (!event.id || knownEventIdsRef.current.has(event.id)) return;

      knownEventIdsRef.current.add(event.id);
      mergeEvents([event]);
      setLatestViolation(event);

      if (event.event_at) {
        latestEventAtRef.current = event.event_at;
      }

      setStats((prev) => ({
        ...prev,
        total_violation_events: prev.total_violation_events + 1,
        students_with_violations: Math.max(prev.students_with_violations, 1),
        violated_students:
          event.test_terminated || event.violation_action === "terminate"
            ? prev.violated_students + 1
            : prev.violated_students,
      }));

      setStudents((prev) => {
        const existing = prev.find((row) => row.student_id === event.student_id);
        if (!existing) {
          return [
            {
              student_id: event.student_id,
              student_name: event.student_name ?? event.student_id,
              violation_count: event.violation_count ?? 1,
              violation_penalty_percent: event.violation_penalty_percent ?? 0,
              score_retention_percent: 100 - (event.violation_penalty_percent ?? 0),
              proctoring_violated: Boolean(event.test_terminated),
              event_counters: { [event.event]: 1 },
              latest_event_at: event.event_at,
              latest_event_type: event.event,
            },
            ...prev,
          ];
        }

        return prev.map((row) => {
          if (row.student_id !== event.student_id) return row;
          const counters = { ...(row.event_counters ?? {}) };
          counters[event.event] = (counters[event.event] ?? 0) + 1;
          return {
            ...row,
            violation_count: event.violation_count ?? row.violation_count + 1,
            violation_penalty_percent:
              event.violation_penalty_percent ?? row.violation_penalty_percent,
            score_retention_percent:
              event.violation_penalty_percent != null
                ? Math.max(0, 100 - event.violation_penalty_percent)
                : row.score_retention_percent,
            proctoring_violated:
              row.proctoring_violated || Boolean(event.test_terminated),
            event_counters: counters,
            latest_event_at: event.event_at ?? row.latest_event_at,
            latest_event_type: event.event ?? row.latest_event_type,
          };
        });
      });

      lastViolationTotalRef.current += 1;
      onNewViolationsRef.current?.();
    },
    [mergeEvents]
  );

  const loadFeed = useCallback(
    async (isInitial = false) => {
      if (!testId) return;
      try {
        const since = isInitial ? null : latestEventAtRef.current;
        const data = await fetchLiveProctoringFeed(
          testId,
          since,
          isInitial ? 500 : 100
        );
        setStudents(data.students ?? []);
        setStats({
          active_sessions: data.active_sessions ?? 0,
          violated_students: data.violated_students ?? 0,
          students_with_violations: data.students_with_violations ?? 0,
          total_violation_events: data.total_violation_events ?? 0,
          max_violation_attempts: data.max_violation_attempts ?? 4,
        });
        setLastFetchedAt(data.fetched_at ?? new Date().toISOString());
        if (data.latest_event_at) {
          latestEventAtRef.current = data.latest_event_at;
        }

        const feedActive = data.live_feed_active ?? true;
        liveFeedActiveRef.current = feedActive;
        setIsLiveFeedActive(feedActive);
        if (!feedActive) {
          stopLiveTransport(wsRef, reconnectTimerRef, setIsLiveConnected);
        }

        const incomingEvents = data.events ?? [];
        if (isInitial) {
          setEvents(incomingEvents);
          knownEventIdsRef.current = new Set(incomingEvents.map((row) => row.id));
          lastViolationTotalRef.current = data.total_violation_events ?? 0;
        } else {
          const freshEvents = incomingEvents.filter(
            (row) => !knownEventIdsRef.current.has(row.id)
          );
          freshEvents.forEach((row) => knownEventIdsRef.current.add(row.id));
          mergeEvents(incomingEvents);

          const violationTotal = data.total_violation_events ?? 0;
          if (
            freshEvents.length > 0 ||
            violationTotal > lastViolationTotalRef.current
          ) {
            lastViolationTotalRef.current = violationTotal;
            onNewViolationsRef.current?.();
          }
        }
        setError(null);
        setIsPollingHealthy(true);
      } catch (err) {
        console.error("Live proctoring feed error:", err);
        setError("Unable to load security logs.");
        setIsPollingHealthy(false);
      } finally {
        setLoading(false);
      }
    },
    [mergeEvents, testId]
  );

  const refresh = useCallback(() => {
    latestEventAtRef.current = null;
    knownEventIdsRef.current = new Set();
    liveFeedActiveRef.current = null;
    setIsLiveFeedActive(null);
    setEvents([]);
    setLoading(true);
    void loadFeed(true);
  }, [loadFeed]);

  useEffect(() => {
    latestEventAtRef.current = null;
    knownEventIdsRef.current = new Set();
    lastViolationTotalRef.current = 0;
    liveFeedActiveRef.current = null;
    setIsLiveFeedActive(null);
    setIsPollingHealthy(false);
    setEvents([]);
    setLatestViolation(null);
    setLoading(true);
    void loadFeed(true);
  }, [loadFeed, testId]);

  useEffect(() => {
    if (paused || !testId || isLiveFeedActive === false) return undefined;

    const pollMs = isLiveConnected ? FALLBACK_POLL_MS : pollIntervalMs;
    const timer = window.setInterval(() => {
      void loadFeed(false);
    }, pollMs);
    return () => window.clearInterval(timer);
  }, [
    isLiveConnected,
    isLiveFeedActive,
    loadFeed,
    paused,
    pollIntervalMs,
    testId,
  ]);

  useEffect(() => {
    if (paused || !testId || isLiveFeedActive === false) {
      stopLiveTransport(wsRef, reconnectTimerRef, setIsLiveConnected);
      return undefined;
    }

    let cancelled = false;
    let attempt = 0;

    const connect = () => {
      if (cancelled || liveFeedActiveRef.current === false) return;
      const url = buildProctoringWebSocketUrl(testId);
      if (!url) {
        setIsLiveConnected(false);
        setError("Unable to connect live feed (missing auth token).");
        return;
      }

      // Drop any previous socket before opening a new one (reconnect path).
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
        wsRef.current = null;
      }

      const socket = new WebSocket(url);
      wsRef.current = socket;

      socket.onopen = () => {
        if (cancelled || liveFeedActiveRef.current === false) {
          socket.close();
          return;
        }
        attempt = 0;
        setIsLiveConnected(true);
        setError(null);
      };

      socket.onmessage = (messageEvent) => {
        const message = parseProctoringWebSocketMessage(messageEvent.data);
        if (!message || message.type !== "NEW_VIOLATION" || !message.data) return;
        applyRealtimeViolation(message.data as LiveProctoringEventItem);
      };

      socket.onclose = () => {
        wsRef.current = null;
        setIsLiveConnected(false);
        if (cancelled || liveFeedActiveRef.current === false) return;
        attempt += 1;
        const delay = Math.min(WS_RECONNECT_MS * attempt, 15000);
        reconnectTimerRef.current = window.setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // onclose will schedule reconnect; avoid double-close races.
        try {
          socket.close();
        } catch {
          /* ignore */
        }
      };
    };

    connect();

    const pingTimer = window.setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send("ping");
      }
    }, 30000);

    return () => {
      cancelled = true;
      window.clearInterval(pingTimer);
      stopLiveTransport(wsRef, reconnectTimerRef, setIsLiveConnected);
    };
  }, [applyRealtimeViolation, isLiveFeedActive, paused, testId]);

  useEffect(() => {
    if (!latestViolation) return undefined;
    const timer = window.setTimeout(() => setLatestViolation(null), 5000);
    return () => window.clearTimeout(timer);
  }, [latestViolation]);

  return {
    events,
    students,
    stats,
    loading,
    error,
    lastFetchedAt,
    isLiveConnected,
    isLiveFeedActive,
    isPollingHealthy,
    latestViolation,
    refresh,
  };
};
